/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "API overview", description = "Hightouch exposes a REST style API that allows users to interact with resources like syncs, models, sources and destinations.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "API overview",
        description: "Hightouch exposes a REST style API that allows users to interact with resources like syncs, models, sources and destinations.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Required permissions",
          "depth": 0,
          "id": "required-permissions"
        }, {
          "value": "Create an API key",
          "depth": 0,
          "id": "create-an-api-key"
        }, {
          "value": "Choose a user group",
          "depth": 1,
          "id": "choose-a-user-group"
        }, {
          "value": "Change a key's user group",
          "depth": 0,
          "id": "change-a-keys-user-group"
        }, {
          "value": "How Hightouch authorizes API keys",
          "depth": 0,
          "id": "how-hightouch-authorizes-api-keys"
        }, {
          "value": "When a group is removed from a workspace",
          "depth": 0,
          "id": "when-a-group-is-removed-from-a-workspace"
        }, {
          "value": "Revoke an API key",
          "depth": 0,
          "id": "revoke-an-api-key"
        }, {
          "value": "Base URL and authentication",
          "depth": 0,
          "id": "base-url-and-authentication"
        }, {
          "value": "Hightouch API Rate Limits",
          "depth": 0,
          "id": "hightouch-api-rate-limits"
        }, {
          "value": "TLS requirements",
          "depth": 0,
          "id": "tls-requirements"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common Errors",
          "depth": 1,
          "id": "common-errors"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      strong: "strong",
      img: "img",
      h3: "h3",
      code: "code",
      ol: "ol",
      pre: "pre",
      span: "span",
      h4: "h4"
    }, _provideComponents(), props.components), {PylonLink, CreateAPIKeySnippet} = _components;
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch exposes a REST API that lets users interact with resources like syncs, models, sources, and destinations.\nThough the REST API is open to all Hightouch users, we aim to build features so you don't need to rely on it directly. For example, ", _jsx(_components.a, {
          href: "/extensions/git-sync",
          children: "Git Sync"
        }), " lets you manage workspace resources programmatically."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're looking to create a custom destination, it's best to use the ", _jsx(_components.a, {
          href: "/destinations/http-request",
          children: "HTTP Request destination"
        }), " as your base. If you find you still need to use the REST API directly, ", _jsx(PylonLink, {
          pylonText: "",
          linkText: "we'd love to hear from you"
        }), " about your use case."]
      }), "\n", _jsx(_components.p, {
        children: "To start using the API, you must first create and retrieve an API key from your workspace."
      }), "\n", _jsx(_components.h2, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Only a workspace admin can create an API key. See ", _jsx(_components.a, {
          href: "/workspace-management/roles#pre-built-roles",
          children: "Roles"
        }), " for more information."]
      }), "\n", _jsx(_components.p, {
        children: "Every API key belongs to one workspace. On each request, Hightouch checks the key's permissions. Those permissions come from one of two places:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "A user group"
          }), " — The key inherits the permissions of a user group in the workspace. Changing the group's role changes what the key can do."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All of the creator's groups"
          }), " — The key inherits the creator's permissions. Changing the creator's permissions changes what the key can do."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Scope a key to a group to keep it from having admin access. For example, an AI tool can use a key scoped to a group with the ", _jsx(_components.strong, {
          children: "Workspace viewer"
        }), " role. The key can read syncs, models, and sources but can't create, edit, or delete them. A deployment pipeline can use a key scoped to a group whose custom role can configure syncs for only the destinations that pipeline owns."]
      }), "\n", _jsx(_components.h2, {
        id: "create-an-api-key",
        children: "Create an API key"
      }), "\n", _jsx(_components.p, {
        children: "Log in to Hightouch as a workspace admin, then follow these steps:"
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "developer-tools/api-keys/dev-api-keys-add.png",
          alt: "API keys tab in Workspace settings with the Add API key button highlighted"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "developer-tools/api-keys/dev-api-keys-create-dialog.png",
          alt: "Create API Key dialog with Name, User group, and the API key to copy"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you have an API key, you can follow the ", _jsx(_components.a, {
          href: "/api-reference",
          children: "API Reference documentation"
        }), " to start using the API."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-a-user-group",
        children: "Choose a user group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "User group"
        }), " field lists every group that has a role in the current workspace, for example ", _jsx(_components.code, {
          children: "Marketing (Workspace editor)"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Organization-level groups, such as ", _jsx(_components.strong, {
          children: "Organization admins"
        }), " and ", _jsx(_components.strong, {
          children: "Organization viewers"
        }), ", don't appear. These groups have access to every workspace in the organization, and a key belongs to one workspace, so their permissions don't fit a single key."]
      }), "\n", _jsx(_components.h2, {
        id: "change-a-keys-user-group",
        children: "Change a key's user group"
      }), "\n", _jsxs(_components.p, {
        children: ["You can move an existing key to a different group, or between a group and ", _jsx(_components.strong, {
          children: "All of the creator's groups"
        }), ", from the key list."]
      }), "\n", _jsx(_components.p, {
        children: "Log in to Hightouch as a workspace admin, then follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/api-keys?from=docs",
            children: [_jsx(_components.strong, {
              children: "API keys"
            }), " tab"]
          }), " on the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page, find the key in the list."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "User group"
          }), " column, select the new group."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), " in the bar at the bottom of the page, then click ", _jsx(_components.strong, {
            children: "Confirm"
          }), " in the dialog."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The change takes effect after a short delay. If you move a key to a group with fewer permissions, anything that authenticates with the key may start receiving errors."
      }), "\n", _jsx(_components.h2, {
        id: "how-hightouch-authorizes-api-keys",
        children: "How Hightouch authorizes API keys"
      }), "\n", _jsx(_components.p, {
        children: "On every request, Hightouch confirms that the key's creator could still perform everything the key can before it applies the key's permissions."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["If the key is scoped to ", _jsx(_components.strong, {
            children: "All of the creator's groups"
          }), ", the key works as long as the creator is still an active user with access to the key's workspace."]
        }), "\n", _jsxs(_components.li, {
          children: ["If the key is scoped to a user group, the key works only if the creator meets one of these conditions in the key's workspace:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "The creator is a member of the key's group."
            }), "\n", _jsx(_components.li, {
              children: "The creator is a workspace admin."
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This rule guarantees that a key can never do more than its creator can. A workspace admin can scope a key to any group in the workspace, including one they don't belong to. They already have permission to perform every action in the workspace, so the key can't exceed their access. If that admin is later demoted and isn't a member of the key's group, the key stops working."
      }), "\n", _jsxs(_components.p, {
        children: ["When a scoped key's creator can no longer perform everything the key can, the key's row on the ", _jsx(_components.strong, {
          children: "API keys"
        }), " tab shows a warning that the key has stopped working. To fix the key, do one of the following:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Move the key to a group the creator belongs to."
        }), "\n", _jsx(_components.li, {
          children: "Add the creator back to the group."
        }), "\n", _jsx(_components.li, {
          children: "Delete the key and create a replacement as a workspace admin."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "when-a-group-is-removed-from-a-workspace",
        children: "When a group is removed from a workspace"
      }), "\n", _jsxs(_components.p, {
        children: ["A key scoped to a group only exists while that group has a role in the workspace. Any of these actions removes that role and ", _jsx(_components.strong, {
          children: "deletes every key scoped to the group in that workspace"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Setting the group's role to ", _jsx(_components.strong, {
            children: "No access"
          }), " on the ", _jsx(_components.strong, {
            children: "Workspace settings > Groups"
          }), " page or on the group's ", _jsx(_components.strong, {
            children: "Workspaces"
          }), " tab in ", _jsx(_components.strong, {
            children: "Organization settings > Groups"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Removing the workspace from the group."
        }), "\n", _jsx(_components.li, {
          children: "Deleting the group."
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Deleting a key can't be undone. Before you remove a group's access, check the\ngroup's ", _jsx(_components.strong, {
            children: "API keys"
          }), " tab in ", _jsx(_components.strong, {
            children: "Organization settings > Groups"
          }), ". Move each\nkey to another group or create a replacement key, then update whatever uses\nit."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "revoke-an-api-key",
        children: "Revoke an API key"
      }), "\n", _jsx(_components.p, {
        children: "Your API key provides access to sensitive Hightouch resources and should be kept secure. If you believe your API key may have been compromised, revoke it immediately."
      }), "\n", _jsx(_components.p, {
        children: "Log in to Hightouch as a workspace admin, then follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/api-keys?from=docs",
            children: [_jsx(_components.strong, {
              children: "API keys"
            }), " tab"]
          }), " on the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page, select the API key or keys you want to revoke."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Delete selected"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the modal that appears, select ", _jsx(_components.strong, {
            children: "Delete"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "base-url-and-authentication",
        children: "Base URL and authentication"
      }), "\n", _jsx(_components.p, {
        children: "Send all API requests to the following base URL:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "https://api.hightouch.com/api/v1\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Append the endpoint path to the base URL. For example, to list syncs, send a request to ", _jsx(_components.code, {
          children: "https://api.hightouch.com/api/v1/syncs"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Authenticate each request by passing your API key as a bearer token in the ", _jsx(_components.code, {
          children: "Authorization"
        }), " header:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["curl https://api.hightouch.com/api/v1/syncs \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Authorization: Bearer YOUR_API_KEY\""
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "hightouch-api-rate-limits",
        children: "Hightouch API Rate Limits"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch API rate limit is 200 requests per 10 seconds per workspace."
      }), "\n", _jsx(_components.h2, {
        id: "tls-requirements",
        children: "TLS requirements"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch REST API requires TLS 1.2 or later. The API rejects requests that use an older protocol version."
      }), "\n", _jsx(_components.p, {
        children: "The API supports the following cipher suites:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "TLS_AES_128_GCM_SHA256\nTLS_AES_256_GCM_SHA384\nTLS_CHACHA20_POLY1305_SHA256\nECDHE-ECDSA-AES128-GCM-SHA256\nECDHE-RSA-AES128-GCM-SHA256\nECDHE-ECDSA-AES256-GCM-SHA384\nECDHE-RSA-AES256-GCM-SHA384\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "On July 10, 2026, Hightouch removes support for several weaker cipher suites. After that date, the API rejects requests that negotiate any of the following:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "ECDHE-ECDSA-AES128-SHA256\nECDHE-RSA-AES128-SHA256\nECDHE-ECDSA-AES256-SHA384\nECDHE-RSA-AES256-SHA384\n"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "If your API client only supports the deprecated cipher suites, update it\nbefore July 10, 2026 to avoid failed requests. Most modern HTTP libraries and\nTLS stacks already negotiate one of the supported suites."
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsxs(_components.p, {
        children: ["If you encounter issues using the Hightouch API, check our ", _jsx(_components.a, {
          href: "https://status.hightouch.io/",
          children: "status page"
        }), " or ", _jsx(PylonLink, {
          pylonText: "Hi, I'm having trouble with the API.",
          linkText: "contact us"
        }), " for assistance."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common Errors"
      }), "\n", _jsx(_components.h4, {
        id: "authentication-error-workspace-is-invalid",
        children: "Authentication error: Workspace is invalid"
      }), "\n", _jsxs(_components.p, {
        children: ["The key's creator no longer has access to the key's workspace. This happens when the creator is deactivated, removed from the organization, or removed from every group that has a role in the workspace, for example after an ", _jsx(_components.a, {
          href: "/workspace-management/sso",
          children: "SSO migration"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Authentication error\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"details\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"No user found\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Authentication error\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"details\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Workspace is invalid\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Either restore the creator's access or create a replacement key as a workspace admin."
      }), "\n", _jsx(_components.h4, {
        id: "forbidden-user-is-not-permitted-to-access-resource",
        children: "Forbidden: User is not permitted to access resource"
      }), "\n", _jsx(_components.p, {
        children: "The key authenticated, but its group doesn't have permission for the action. For example, a key scoped to a Workspace viewer group can list sources but receives this error when it tries to update one."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Forbidden\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"details\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"User is not permitted to access resource.\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Check the group's role in ", _jsx(_components.strong, {
          children: "Organization settings > Groups"
        }), ". Either give the group the missing permission, or move the key to a group that has it."]
      }), "\n", _jsxs(_components.p, {
        children: ["If the group does have the permission, the key's creator may no longer meet the ", _jsx(_components.a, {
          href: "#how-hightouch-authorizes-api-keys",
          children: "authorization rule"
        }), ". Check the ", _jsx(_components.strong, {
          children: "API keys"
        }), " tab for a warning next to the key."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
